// CR075-P4: Deduped Deals View with Review Controls + Promote to Admin (CR165)
import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { 
  Search, ChevronDown, Download, AlertTriangle, CheckCircle2, 
  DollarSign, Calendar, Tag, ExternalLink, GitMerge, Trophy, AlertCircle,
  XCircle, ArrowRight, Loader2, FileImage
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useSiteState } from "@/hooks/useSiteState";

interface MergedSource {
  source_type: string;
  source_url: string | null;
  deal_link: string | null;
  staging_item_id: string;
  is_winner: boolean;
  completeness_score: number;
}

interface ConflictFlags {
  value_conflict: boolean;
  date_conflict: boolean;
  brand_conflict: boolean;
  details: string[];
}

interface CanonicalDeal {
  schema_version: string;
  merchant_key: string;
  merchant_name: string;
  deal_type: string;
  headline_mechanic: string;
  value: {
    computed_value_cad: number | null;
    effective_percent_back: number | null;
    pay_amount_cad: number | null;
    receive_value_cad: number | null;
    bonus_value_cad: number | null;
    discount_percent: number | null;
    qualifying_spend_cad: number | null;
  };
  brands: {
    purchase_brand: string | null;
    bonus_brand: string | null;
  };
  dates: {
    valid_from: string | null;
    valid_until: string | null;
  };
  sources: Array<{
    source_type: string;
    source_url: string | null;
    deal_link: string | null;
    staging_item_id: string;
  }>;
  dedupe_fingerprint_inputs: {
    merchant_key: string;
    purchase_brand: string | null;
    headline_normalized: string;
    value_key: string;
    valid_until: string | null;
  };
  // CR134: Canonical economics fingerprint for debugging
  dedup_fingerprint?: string | null;
  dedup_fingerprint_components?: {
    merchant_key?: string | null;
    benefit_type?: string | null;
    payout_method?: string | null;
    benefit_value?: number | null;
    threshold_amount?: number | null;
    reward_brand?: string | null;
    // CR187: purchase_brand for gift card promos
    purchase_brand?: string | null;
  } | null;
  publish: {
    is_publishable: boolean;
    missing_fields: string[];
    warnings: string[];
  };
  claim_url: string | null;
}

// CR437: Flyer snippet data from admin-get-deduped-flyer-snippets edge function
interface FlyerSnippetData {
  snippet_asset_path: string | null;
  page_image_path: string | null;
  page_number: number | null;
  bbox_norm: any | null;
  snippet_status: string | null;
}

interface DedupedRow {
  id: string;
  batch_run_id: string;
  dedupe_key: string;
  canonical_deal_json: CanonicalDeal;
  merged_sources_json: MergedSource[];
  dedupe_rules_version: string;
  conflict_flags_json: ConflictFlags | null;
  computed_value_cad: number | null;
  effective_percent_back: number | null;
  review_status: string;
  review_reason: string | null;
  reviewed_by: string | null;
  reviewed_at: string | null;
  publish_status: string;
  published_at: string | null;
  created_at: string;
  // CR435: Injected flyer snippet
  flyer_snippet?: FlyerSnippetData | null;
}

interface DedupedDealsViewProps {
  batchRunId: string;
  onPromoteComplete?: () => void; // CR165: Callback to refresh parent steps after S7A
}

const REJECTION_REASONS = [
  { value: "DUPLICATE", label: "Duplicate of existing deal" },
  { value: "EXPIRED", label: "Already expired" },
  { value: "INVALID_DATA", label: "Invalid or incorrect data" },
  { value: "LOW_VALUE", label: "Too low value" },
  { value: "NOT_APPLICABLE", label: "Not applicable for Canada" },
  { value: "OTHER", label: "Other reason" },
];

export function DedupedDealsView({ batchRunId, onPromoteComplete }: DedupedDealsViewProps) {
  const [deals, setDeals] = useState<DedupedRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [activeTab, setActiveTab] = useState<"accepted" | "rejected">("accepted");
  const [promoteDialogOpen, setPromoteDialogOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");

  // CR165: Use site state hook for S7A promotion
  const { promoteToAdmin, promoting, siteState } = useSiteState();

  // CR165: Check if this batch is already promoted to Admin
  const isAlreadyPromoted = siteState?.admin_batch_run_id === batchRunId;

  useEffect(() => {
    fetchDeals();
  }, [batchRunId]);

  const fetchDeals = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("cr075_staging_deals_deduped")
        .select("*")
        .eq("batch_run_id", batchRunId)
        .order("created_at", { ascending: false });

      if (error) throw error;
      const rawDeals = (data || []) as unknown as DedupedRow[];

      // CR438: Fetch flyer snippets via admin edge function (server-side join, bypasses RLS)
      // Handles both wrapped ({ data: { snippetsByDedupedId } }) and unwrapped ({ snippetsByDedupedId }) response shapes
      let snippetsByDedupedId: Record<string, FlyerSnippetData> = {};
      try {
        const { data: fnData, error: fnError } = await supabase.functions.invoke(
          "admin-get-deduped-flyer-snippets",
          { body: { batch_run_id: batchRunId } }
        );
        if (fnError) {
          console.warn("CR438: S6 snippets fetch failed:", fnError);
        } else {
          // Support both wrapped and unwrapped response formats
          const payload = (fnData && typeof fnData === "object" && "data" in fnData)
            ? (fnData as any).data
            : fnData;
          snippetsByDedupedId = payload?.snippetsByDedupedId ?? {};
          const meta = payload?.meta;
          if (meta) {
            console.log("CR438: Snippet hydration meta:", meta);
          }
        }
      } catch (snippetErr) {
        console.warn("CR438: Failed to fetch flyer snippets:", snippetErr);
      }

      // CR437: Attach snippets directly by deduped row ID
      const dealsWithSnippets = rawDeals.map(d => ({
        ...d,
        flyer_snippet: snippetsByDedupedId[d.id] ?? null,
      }));

      setDeals(dealsWithSnippets);
    } catch (err) {
      console.error("Failed to fetch deduped deals:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleReject = async (dealId: string, reason: string, customReason?: string) => {
    try {
      const finalReason = reason === "OTHER" && customReason ? customReason : reason;
      
      const { error } = await supabase
        .from("cr075_staging_deals_deduped")
        .update({
          review_status: "REJECTED",
          review_reason: finalReason,
          reviewed_at: new Date().toISOString(),
          reviewed_by: "admin",
        })
        .eq("id", dealId);

      if (error) throw error;
      
      toast.success("Deal rejected");
      fetchDeals();
    } catch (err) {
      console.error("Failed to reject deal:", err);
      toast.error("Failed to reject deal");
    }
  };

  const handleAccept = async (dealId: string) => {
    try {
      const { error } = await supabase
        .from("cr075_staging_deals_deduped")
        .update({
          review_status: "ACCEPTED",
          review_reason: null,
          reviewed_at: new Date().toISOString(),
          reviewed_by: "admin",
        })
        .eq("id", dealId);

      if (error) throw error;
      
      toast.success("Deal accepted");
      fetchDeals();
    } catch (err) {
      console.error("Failed to accept deal:", err);
      toast.error("Failed to accept deal");
    }
  };

  // CR165: Handle S7A promotion (replaces direct publish)
  const handlePromoteToAdmin = async () => {
    if (confirmText !== "PROMOTE") {
      toast.error("Please type PROMOTE to confirm");
      return;
    }

    const result = await promoteToAdmin(batchRunId, { 
      notes: `Promoted from Deduped Deals (S5) screen` 
    });
    
    if (result.success) {
      setPromoteDialogOpen(false);
      setConfirmText("");
      fetchDeals();
      
      // Notify parent to refresh steps panel (S7A now COMPLETE)
      if (onPromoteComplete) {
        onPromoteComplete();
      }
    }
  };

  const toggleExpand = (id: string) => {
    const newExpanded = new Set(expandedIds);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedIds(newExpanded);
  };

  const acceptedDeals = deals.filter(d => d.review_status === "ACCEPTED");
  const rejectedDeals = deals.filter(d => d.review_status === "REJECTED");

  useEffect(() => {
    // Keep S6 review visible when default tab has no rows but the other tab does.
    if (activeTab === "accepted" && acceptedDeals.length === 0 && rejectedDeals.length > 0) {
      setActiveTab("rejected");
    }
    if (activeTab === "rejected" && rejectedDeals.length === 0 && acceptedDeals.length > 0) {
      setActiveTab("accepted");
    }
  }, [activeTab, acceptedDeals.length, rejectedDeals.length]);

  const currentDeals = activeTab === "accepted" ? acceptedDeals : rejectedDeals;

  const filteredDeals = currentDeals.filter(d => {
    if (!searchTerm) return true;
    const deal = d.canonical_deal_json;
    const search = searchTerm.toLowerCase();
    return (
      deal.merchant_name.toLowerCase().includes(search) ||
      deal.headline_mechanic.toLowerCase().includes(search) ||
      (deal.brands.purchase_brand?.toLowerCase().includes(search) ?? false)
    );
  });

  const exportJson = () => {
    const blob = new Blob([JSON.stringify(deals, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `deduped_deals_${batchRunId}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const formatValue = (deal: CanonicalDeal): string => {
    if (deal.value.computed_value_cad) {
      return `$${deal.value.computed_value_cad.toFixed(2)}`;
    }
    if (deal.value.effective_percent_back) {
      return `${deal.value.effective_percent_back}%`;
    }
    return "No value";
  };

  const getSourceIcon = (sourceType: string) => {
    switch (sourceType) {
      case "FLYER": return "📄";
      case "WEBSITE_PROMPT_PACK": return "🌐";
      case "FORUM_SIGNALS": return "💬";
      default: return "❓";
    }
  };

  const hasConflicts = (row: DedupedRow): boolean => {
    const flags = row.conflict_flags_json;
    return !!(flags?.value_conflict || flags?.date_conflict || flags?.brand_conflict);
  };

  // CR165: Get CTA button state
  const getPromoteButtonState = (): { disabled: boolean; label: string } => {
    if (isAlreadyPromoted) {
      return { disabled: true, label: "Already Promoted" };
    }
    if (acceptedDeals.length === 0) {
      return { disabled: true, label: "No Accepted Deals" };
    }
    if (promoting) {
      return { disabled: true, label: "Promoting..." };
    }
    return { disabled: false, label: `Promote ${acceptedDeals.length} to Admin` };
  };

  const promoteButtonState = getPromoteButtonState();

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <GitMerge className="h-5 w-5" />
              Deduped Deals (S5)
              <Badge variant="outline">{deals.length}</Badge>
            </CardTitle>
            <CardDescription>
              Review and publish merged deals
            </CardDescription>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={exportJson} className="gap-2">
              <Download className="h-4 w-4" />
              Export JSON
            </Button>
            <div className="flex flex-col items-end gap-1">
              <Button 
                size="sm" 
                className="gap-2"
                onClick={() => setPromoteDialogOpen(true)}
                disabled={promoteButtonState.disabled}
              >
                {promoting ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <ArrowRight className="h-4 w-4" />
                )}
                {promoteButtonState.label}
              </Button>
              <span className="text-xs text-muted-foreground">
                Step S7A → Does not publish Live
              </span>
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Tabs for Accepted/Rejected */}
        <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as "accepted" | "rejected")}>
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="accepted" className="gap-2">
              <CheckCircle2 className="h-4 w-4" />
              Accepted ({acceptedDeals.length})
            </TabsTrigger>
            <TabsTrigger value="rejected" className="gap-2">
              <XCircle className="h-4 w-4" />
              Rejected ({rejectedDeals.length})
            </TabsTrigger>
          </TabsList>
        </Tabs>

        <div className="relative">
          <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by merchant, headline, or brand..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>

        {loading ? (
          <div className="text-center py-8 text-muted-foreground">Loading...</div>
        ) : filteredDeals.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            No {activeTab} deals found
          </div>
        ) : (
          <ScrollArea className="h-[500px]">
            <div className="space-y-3">
              {filteredDeals.map((row) => (
                <DealRow
                  key={row.id}
                  row={row}
                  isExpanded={expandedIds.has(row.id)}
                  onToggle={() => toggleExpand(row.id)}
                  onReject={handleReject}
                  onAccept={handleAccept}
                  formatValue={formatValue}
                  getSourceIcon={getSourceIcon}
                  hasConflicts={hasConflicts}
                  isRejectedTab={activeTab === "rejected"}
                />
              ))}
            </div>
          </ScrollArea>
        )}
      </CardContent>

      {/* CR165: Promote to Admin Confirmation Dialog (S7A) */}
      <Dialog open={promoteDialogOpen} onOpenChange={setPromoteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Promote Deals to Admin</DialogTitle>
            <DialogDescription className="space-y-2">
              <p>You are about to promote <strong>{acceptedDeals.length}</strong> accepted deals to Admin.</p>
              <p><strong>{rejectedDeals.length}</strong> rejected deals will be excluded.</p>
              <p className="text-sm text-muted-foreground mt-4">
                This will move deals into the Admin review queue for final checks before publishing Live (S7B).
              </p>
              <p className="text-xs text-muted-foreground">
                <strong>Note:</strong> The deals will NOT be visible to the public until you complete S7B (Publish to Live).
              </p>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="confirm-promote">Type <strong>PROMOTE</strong> to confirm</Label>
              <Input
                id="confirm-promote"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder="PROMOTE"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setPromoteDialogOpen(false); setConfirmText(""); }}>
              Cancel
            </Button>
            <Button 
              variant="secondary"
              onClick={handlePromoteToAdmin} 
              disabled={confirmText !== "PROMOTE" || promoting}
            >
              {promoting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Promoting...
                </>
              ) : (
                "Promote"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

// Extracted DealRow component for cleaner code
interface DealRowProps {
  row: DedupedRow;
  isExpanded: boolean;
  onToggle: () => void;
  onReject: (id: string, reason: string, customReason?: string) => void;
  onAccept: (id: string) => void;
  formatValue: (deal: CanonicalDeal) => string;
  getSourceIcon: (type: string) => string;
  hasConflicts: (row: DedupedRow) => boolean;
  isRejectedTab: boolean;
}

function DealRow({
  row,
  isExpanded,
  onToggle,
  onReject,
  onAccept,
  formatValue,
  getSourceIcon,
  hasConflicts,
  isRejectedTab,
}: DealRowProps) {
  const [rejectReason, setRejectReason] = useState("");
  const [customReason, setCustomReason] = useState("");
  const [showRejectForm, setShowRejectForm] = useState(false);

  const deal = row.canonical_deal_json;
  const sources = row.merged_sources_json || [];
  const conflicts = row.conflict_flags_json;

  return (
    <Collapsible open={isExpanded} onOpenChange={onToggle}>
      <div className={`border rounded-lg ${hasConflicts(row) ? "border-yellow-500" : ""} ${isRejectedTab ? "opacity-75" : ""}`}>
        <CollapsibleTrigger asChild>
          <button className="w-full p-4 flex items-center justify-between text-left hover:bg-muted/50 transition-colors">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className="font-medium">{deal.merchant_name}</span>
                <Badge variant="outline" className="text-xs">{deal.deal_type}</Badge>
                {sources.length > 1 && (
                  <Badge variant="secondary" className="text-xs">
                    {sources.length} sources
                  </Badge>
                )}
                {hasConflicts(row) && (
                  <AlertCircle className="h-4 w-4 text-yellow-500" />
                )}
                {row.publish_status === "PUBLISHED" && (
                  <Badge className="bg-green-600 text-xs">Published</Badge>
                )}
              </div>
              <div className="text-sm text-muted-foreground truncate">
                {deal.headline_mechanic}
              </div>
              {isRejectedTab && row.review_reason && (
                <div className="text-xs text-destructive mt-1">
                  Reason: {row.review_reason}
                </div>
              )}
            </div>
            <div className="flex items-center gap-4">
              <div className="text-right">
                <div className="font-medium text-primary">{formatValue(deal)}</div>
                <div className="text-xs text-muted-foreground flex gap-1">
                  {sources.slice(0, 3).map((s, i) => (
                    <span key={i}>{getSourceIcon(s.source_type)}</span>
                  ))}
                </div>
              </div>
              <ChevronDown className={`h-4 w-4 transition-transform ${isExpanded ? "rotate-180" : ""}`} />
            </div>
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="px-4 pb-4 pt-2 border-t space-y-4">
            {/* Review Actions */}
            {row.publish_status !== "PUBLISHED" && (
              <div className="flex items-center gap-4 p-3 bg-muted/50 rounded">
                {isRejectedTab ? (
                  <Button size="sm" variant="outline" onClick={() => onAccept(row.id)}>
                    <CheckCircle2 className="h-4 w-4 mr-2" />
                    Accept
                  </Button>
                ) : (
                  <>
                    {!showRejectForm ? (
                      <Button 
                        size="sm" 
                        variant="destructive" 
                        onClick={() => setShowRejectForm(true)}
                      >
                        <XCircle className="h-4 w-4 mr-2" />
                        Reject
                      </Button>
                    ) : (
                      <div className="flex-1 space-y-2">
                        <Select value={rejectReason} onValueChange={setRejectReason}>
                          <SelectTrigger className="w-full">
                            <SelectValue placeholder="Select reason..." />
                          </SelectTrigger>
                          <SelectContent>
                            {REJECTION_REASONS.map((r) => (
                              <SelectItem key={r.value} value={r.value}>
                                {r.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {rejectReason === "OTHER" && (
                          <Textarea
                            placeholder="Enter custom reason..."
                            value={customReason}
                            onChange={(e) => setCustomReason(e.target.value)}
                            className="h-20"
                          />
                        )}
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="destructive"
                            disabled={!rejectReason || (rejectReason === "OTHER" && !customReason)}
                            onClick={() => {
                              onReject(row.id, rejectReason, customReason);
                              setShowRejectForm(false);
                              setRejectReason("");
                              setCustomReason("");
                            }}
                          >
                            Confirm Reject
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setShowRejectForm(false);
                              setRejectReason("");
                              setCustomReason("");
                            }}
                          >
                            Cancel
                          </Button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {/* CR435: Flyer Snippet Thumbnail */}
            {row.flyer_snippet && (() => {
              const snippetPath =
                row.flyer_snippet.snippet_asset_path ??
                (deal as any).artifacts?.flyer_snippet?.asset_path ??
                null;
              const pageImagePath = row.flyer_snippet.page_image_path;
              const snippetStatus = row.flyer_snippet.snippet_status;

              if (snippetPath) {
                const { data: urlData } = supabase.storage
                  .from("flyer-snippets")
                  .getPublicUrl(snippetPath);
                return (
                  <div className="space-y-1">
                    <div className="text-sm font-medium flex items-center gap-2">
                      <FileImage className="h-4 w-4" />
                      Flyer Snippet
                      {row.flyer_snippet.page_number != null && (
                        <Badge variant="outline" className="text-xs">
                          Page {row.flyer_snippet.page_number}
                        </Badge>
                      )}
                    </div>
                    <div className="rounded-lg border bg-muted/30 overflow-hidden max-w-xs">
                      <img
                        src={urlData.publicUrl}
                        alt="Flyer snippet"
                        className="w-full max-h-40 object-contain"
                        loading="lazy"
                      />
                    </div>
                  </div>
                );
              }

              // CR436: Show deferred status badge
              if (snippetStatus === "deferred_needs_preview") {
                return (
                  <div className="text-xs text-muted-foreground flex items-center gap-1.5">
                    <FileImage className="h-3.5 w-3.5" />
                    <Badge variant="secondary" className="text-xs">Deferred (needs thumbnail)</Badge>
                    {row.flyer_snippet.page_number != null && (
                      <span>· Page {row.flyer_snippet.page_number}</span>
                    )}
                  </div>
                );
              }

              // Fallback: show page_image_path indicator if no snippet yet
              if (pageImagePath) {
                return (
                  <div className="text-xs text-muted-foreground flex items-center gap-1.5">
                    <FileImage className="h-3.5 w-3.5" />
                    Flyer page available (snippet not yet cropped)
                    {row.flyer_snippet.page_number != null && (
                      <span>· Page {row.flyer_snippet.page_number}</span>
                    )}
                  </div>
                );
              }

              return null;
            })()}

            {/* Dedupe Key & Rules Version */}
            <div className="p-3 bg-muted/50 rounded text-xs">
              <div className="flex justify-between mb-2">
                <span className="text-muted-foreground">Dedupe Key:</span>
                <code className="font-mono">{row.dedupe_key}</code>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Rules Version:</span>
                <code className="font-mono">{row.dedupe_rules_version}</code>
              </div>
            </div>

            {/* CR134: Canonical Economics Fingerprint for debugging */}
            {deal.dedup_fingerprint && (
              <div className="p-3 bg-blue-50 dark:bg-blue-950 rounded text-xs border border-blue-200 dark:border-blue-800">
                <div className="text-sm font-medium flex items-center gap-2 mb-2 text-blue-700 dark:text-blue-300">
                  <Tag className="h-4 w-4" />
                  Canonical Economics Fingerprint
                </div>
                <code className="font-mono text-xs break-all block mb-2">{deal.dedup_fingerprint}</code>
                {deal.dedup_fingerprint_components && (
                  <div className="grid grid-cols-2 gap-1 text-muted-foreground">
                    <div>benefit_type: <span className="text-foreground">{deal.dedup_fingerprint_components.benefit_type || "-"}</span></div>
                    <div>payout: <span className="text-foreground">{deal.dedup_fingerprint_components.payout_method || "-"}</span></div>
                    <div>benefit_value: <span className="text-foreground">{deal.dedup_fingerprint_components.benefit_value ?? "-"}</span></div>
                    <div>threshold: <span className="text-foreground">{deal.dedup_fingerprint_components.threshold_amount ?? "-"}</span></div>
                    {/* CR187: Show purchase_brand when it participates in dedup fingerprint */}
                    {deal.dedup_fingerprint_components.purchase_brand && (
                      <div className="col-span-2 text-purple-600 dark:text-purple-400 font-medium">
                        purchase_brand: <span className="text-foreground">{deal.dedup_fingerprint_components.purchase_brand}</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Dedupe Fingerprint Inputs (legacy) */}
            <div className="space-y-2">
              <div className="text-sm font-medium flex items-center gap-2">
                <Tag className="h-4 w-4" />
                Dedupe Fingerprint Inputs
              </div>
              <div className="p-3 bg-muted/30 rounded text-xs font-mono grid grid-cols-2 gap-2">
                <div>merchant: {deal.dedupe_fingerprint_inputs.merchant_key}</div>
                {/* CR187: Highlight purchase_brand when present - key for gift card dedup */}
                <div className={deal.dedupe_fingerprint_inputs.purchase_brand ? "text-purple-600 dark:text-purple-400 font-semibold" : ""}>
                  brand: {deal.dedupe_fingerprint_inputs.purchase_brand || <span className="text-muted-foreground">none</span>}
                </div>
                <div>headline: {deal.dedupe_fingerprint_inputs.headline_normalized?.slice(0, 25)}...</div>
                <div>value: {deal.dedupe_fingerprint_inputs.value_key}</div>
              </div>
            </div>

            <Separator />

            {/* Winner & Contributors */}
            <div className="space-y-2">
              <div className="text-sm font-medium flex items-center gap-2">
                <Trophy className="h-4 w-4 text-yellow-500" />
                Winner & Contributors
              </div>
              <div className="space-y-2">
                {sources.map((source, idx) => (
                  <div
                    key={idx}
                    className={`p-3 rounded border ${source.is_winner ? "bg-green-50 border-green-200 dark:bg-green-950 dark:border-green-800" : "bg-muted/30"}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span>{getSourceIcon(source.source_type)}</span>
                        <span className="font-medium">{source.source_type}</span>
                        {source.is_winner && (
                          <Badge className="bg-green-500 text-xs">Winner</Badge>
                        )}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        Score: {source.completeness_score}
                      </div>
                    </div>
                    {source.deal_link && (
                      <a
                        href={source.deal_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-primary hover:underline truncate block mt-1"
                      >
                        {source.deal_link}
                      </a>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Conflict Highlighting */}
            {hasConflicts(row) && conflicts && (
              <>
                <Separator />
                <div className="space-y-2">
                  <div className="text-sm font-medium flex items-center gap-2 text-yellow-600">
                    <AlertTriangle className="h-4 w-4" />
                    Conflicts Detected
                  </div>
                  <div className="p-3 bg-yellow-50 dark:bg-yellow-950 rounded border border-yellow-200 dark:border-yellow-800">
                    <div className="flex flex-wrap gap-2 mb-2">
                      {conflicts.value_conflict && (
                        <Badge variant="outline" className="text-yellow-600 border-yellow-600">
                          <DollarSign className="h-3 w-3 mr-1" />
                          Value Conflict
                        </Badge>
                      )}
                      {conflicts.date_conflict && (
                        <Badge variant="outline" className="text-yellow-600 border-yellow-600">
                          <Calendar className="h-3 w-3 mr-1" />
                          Date Conflict
                        </Badge>
                      )}
                      {conflicts.brand_conflict && (
                        <Badge variant="outline" className="text-yellow-600 border-yellow-600">
                          <Tag className="h-3 w-3 mr-1" />
                          Brand Conflict
                        </Badge>
                      )}
                    </div>
                    {conflicts.details.length > 0 && (
                      <div className="text-xs text-yellow-700 dark:text-yellow-300">
                        {conflicts.details.map((d, i) => (
                          <div key={i}>{d}</div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}

            {/* Value Summary */}
            <div className="grid grid-cols-3 gap-4 text-sm">
              <div>
                <div className="text-muted-foreground">Computed Value</div>
                <div className="font-medium">
                  {deal.value.computed_value_cad ? `$${deal.value.computed_value_cad}` : "-"}
                </div>
              </div>
              <div>
                <div className="text-muted-foreground">Effective %</div>
                <div className="font-medium">
                  {deal.value.effective_percent_back ? `${deal.value.effective_percent_back}%` : "-"}
                </div>
              </div>
              <div>
                <div className="text-muted-foreground">Valid Until</div>
                <div className="font-medium">{deal.dates.valid_until || "-"}</div>
              </div>
            </div>

            {/* Claim URL */}
            {deal.claim_url && (
              <Button variant="outline" size="sm" asChild className="gap-2">
                <a href={deal.claim_url} target="_blank" rel="noopener noreferrer">
                  <ExternalLink className="h-4 w-4" />
                  View Deal
                </a>
              </Button>
            )}
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
}
